import React from "react";
import { observer } from "mobx-react";
import { Grid, Box } from "@mui/material";
import { useRootStore } from "../../../../store/contexts";
import { GroupContainer } from "../../../../common/components";
import { FormToolbox } from "../../../../common/controls/forms/FormToolbox";
import { FormSaveFooter } from "../../../../common/controls/forms/FormSaveFooter";

import FormExtras from "../../../../common/components/formExtras";
import { WorkAuthorPublisherCreateStore } from "../../store";
import { InputReference } from "../../../common/controls";
import { CreateEntityDialog } from "../../../common/controls";
import { PublisherCreate } from "../../../publisher/components/create/PublisherCreate";
import { PublisherCreateStore } from "../../../publisher/store";
import { InputNumber } from "../../../common/controls";
import { InputIntegerChoices } from "../../../common/controls";
import { FormControl } from "@mui/material";

export type WorkAuthorPublisherCreateFormProps = {
  onSave?: () => void;
  onCancel?: () => void;
  /** Mientras se guarda, Guardar queda deshabilitado. */
  saving?: boolean;
  showToolbox?: boolean;
  showSaveFooter?: boolean;
  publisherSelectedValue?: { displayId: string; id: string } | null;
  onPublisherSelected?: (
    value: { displayId: string; id: string } | null
  ) => void;
  onPublisherChange?: (value: string) => void;
  publisherOptions?: { displayId: string; id: string }[];
  publisherError?: string;
  percentageSelectedValue?: { displayId: string; id: string } | null;
  onPercentageChange?: (value: number) => void;
  percentage: number;
  percentageError?: string;
  durationInYearsSelectedValue?: { displayId: string; id: string } | null;
  onDurationInYearsChange?: (value: number | undefined) => void;
  durationInYears: number | undefined;
  durationInYearsError?: string;
  autoRenewalYearsSelectedValue?: { displayId: string; id: string } | null;
  onAutoRenewalYearsChange?: (value: number | undefined) => void;
  autoRenewalYears: number | undefined;
  autoRenewalYearsError?: string;
  workAuthorPublisherCreateStore: WorkAuthorPublisherCreateStore;
};

export const WorkAuthorPublisherCreateForm: React.FC<WorkAuthorPublisherCreateFormProps> =
  observer(
    ({
      onSave = () => {},
      onCancel = () => {},
      saving = false,
      showToolbox = true,
      showSaveFooter,
      onPublisherSelected = () => {},
      publisherSelectedValue = null,
      publisherOptions = [],
      publisherError,
      onPublisherChange = () => {},
      percentage = 0,
      percentageError,
      onPercentageChange = () => {},
      durationInYears = undefined,
      durationInYearsError,
      onDurationInYearsChange = () => {},
      autoRenewalYears = undefined,
      autoRenewalYearsError,
      onAutoRenewalYearsChange = () => {},
      workAuthorPublisherCreateStore,
    }) => {
      const rootStore = useRootStore();
      const { langStore } = rootStore;

      return (
        <Grid>
          {showToolbox && (
            <FormToolbox
              saveAndCloseLabel={langStore.saveEntity}
              closeLabel={langStore.cancelNewEntity}
              saveForm={() => onSave()}
              saveDisabled={saving}
              closeForm={() => onCancel()}
            />
          )}
          <Box>
            <FormExtras
              entity="WorkAuthorPublisher"
              mode="create"
              values={{}}
              options={{ publisher: publisherOptions }}
              onPatch={(campo: string, valor: any) => {
                switch (campo) {
                  case "publisher":
                    onPublisherSelected?.(valor);
                    break;
                  case "percentage":
                    onPercentageChange?.(valor);
                    break;
                  case "durationInYears":
                    onDurationInYearsChange?.(valor);
                    break;
                  case "autoRenewalYears":
                    onAutoRenewalYearsChange?.(valor);
                    break;
                }
              }}
            />
            <GroupContainer
              id="null"
              errorText={workAuthorPublisherCreateStore.getCardErrors("null")}
              title={langStore.groupWorkAuthorPublisherLabel}
              collapsible={false}
            >
              <FormControl fullWidth>
                <InputReference
                  id="publisher"
                  label={langStore.WorkAuthorPublisherPublisherLabel}
                  autoFocus
                  error={Boolean(publisherError)}
                  helperText={
                    publisherError ||
                    workAuthorPublisherCreateStore.warnings["publisher"]
                      ?.message
                  }
                  selectedValue={publisherSelectedValue}
                  onChange={(value: string) => onPublisherChange(value)}
                  onSelect={(value: { displayId: string; id: string } | null) =>
                    onPublisherSelected(value)
                  }
                  options={publisherOptions}
                  description={
                    langStore.WorkAuthorPublisherPublisherDescription
                  }
                  canCreate={rootStore.authStore.hasPermissions(
                    "publisher_create"
                  )}
                  renderCreateDialog={(dialogProps) => (
                    <CreateEntityDialog
                      {...dialogProps}
                      createStore={(rootStore) =>
                        new PublisherCreateStore(rootStore)
                      }
                      renderForm={(store) => (
                        <PublisherCreate
                          publisherCreateStore={store}
                          showToolbox={false}
                          showSaveFooter={false}
                        />
                      )}
                      saveMethod="saveNewPublisher"
                      cancelMethod="cancelNewPublisher"
                    />
                  )}
                />
                <InputNumber
                  value={percentage}
                  error={Boolean(percentageError)}
                  warning={
                    !percentageError &&
                    Boolean(
                      workAuthorPublisherCreateStore.warnings["percentage"]
                    )
                  }
                  helperText={
                    percentageError ||
                    workAuthorPublisherCreateStore.warnings["percentage"]
                      ?.message
                  }
                  allowNegative={true}
                  label={langStore.WorkAuthorPublisherPercentageLabel}
                  decimalScale={2}
                  description={
                    langStore.WorkAuthorPublisherPercentageDescription
                  }
                  onChange={(value: number) => onPercentageChange(value)}
                />
                <InputIntegerChoices
                  value={durationInYears}
                  nullable={true}
                  min={1}
                  max={100}
                  unitSingular={"año"}
                  unitPlural={"años"}
                  error={Boolean(durationInYearsError)}
                  helperText={
                    durationInYearsError ||
                    workAuthorPublisherCreateStore.warnings["durationInYears"]
                      ?.message
                  }
                  label={langStore.WorkAuthorPublisherDurationInYearsLabel}
                  nullText={
                    langStore.WorkAuthorPublisherDurationInYearsNullText
                  }
                  description={
                    langStore.WorkAuthorPublisherDurationInYearsDescription
                  }
                  onChange={(value: number | undefined) =>
                    onDurationInYearsChange(value)
                  }
                />
                <InputIntegerChoices
                  value={autoRenewalYears}
                  nullable={true}
                  min={1}
                  max={50}
                  unitSingular={"año"}
                  unitPlural={"años"}
                  error={Boolean(autoRenewalYearsError)}
                  helperText={
                    autoRenewalYearsError ||
                    workAuthorPublisherCreateStore.warnings["autoRenewalYears"]
                      ?.message
                  }
                  label={langStore.WorkAuthorPublisherAutoRenewalYearsLabel}
                  nullText={
                    langStore.WorkAuthorPublisherAutoRenewalYearsNullText
                  }
                  description={
                    langStore.WorkAuthorPublisherAutoRenewalYearsDescription
                  }
                  onChange={(value: number | undefined) =>
                    onAutoRenewalYearsChange(value)
                  }
                />
              </FormControl>
            </GroupContainer>
          </Box>
          {(showSaveFooter ?? !showToolbox) && (
            <FormSaveFooter
              onSave={() => onSave()}
              label={langStore.saveEntity}
              disabled={saving}
            />
          )}
        </Grid>
      );
    }
  );
